import type { SocialCapacityPlatform, SocialCapacityPlatformState } from '../../lib/api/dashboard.js';

/**
 * Social posting per platform on one masthead card (CS567, Steve 2026-11-02): "Graphs like the article mix …
 * Use 50 as the social posting limit for all in the graph, including X". The bar is a fixed 0–50 scale for every
 * platform, so mastheads and platforms compare at a glance. CS568 (Steve 04:14:44Z): "When we have a limit of 50,
 * there is no target … The articles is where we have a target", so socials draw no target, only posts against 50.
 */
const SCALE = 50;

const PLATFORM_LABEL: Record<SocialCapacityPlatform, string> = { linkedin: 'LinkedIn', twitter: 'X' };

function pct(n: number): number {
  return Math.min(100, Math.max(0, (n / SCALE) * 100));
}

export function SocialPostingBars({
  platforms,
}: {
  platforms: Partial<Record<SocialCapacityPlatform, SocialCapacityPlatformState>>;
}) {
  const rows = (Object.keys(PLATFORM_LABEL) as SocialCapacityPlatform[]).flatMap((p) =>
    platforms[p] ? [[p, platforms[p]!] as const] : [],
  );
  if (rows.length === 0) return <p className="text-xs text-grey-400">Socials: no platforms reported</p>;
  return (
    <div className="space-y-2">
      {rows.map(([p, s]) => {
        const waiting =
          s.drafts_waiting.coverage + s.drafts_waiting.time_critical + s.drafts_waiting.fresh + s.drafts_waiting.backlog;
        // counted_24h is what the cap counts: posted in the rolling 24h PLUS still queued in Buffer
        // (publishing services/social/capacity.ts). Split it rather than stacking the queue on top.
        const counted = s.counted_24h;
        const posted = Math.max(0, counted - s.queued_in_buffer);
        const postedPct = pct(posted);
        const bufferPct = Math.max(0, pct(counted) - postedPct);
        return (
          <div key={p}>
            <div className="flex items-baseline justify-between text-xs">
              <span className="font-semibold text-grey-700">{PLATFORM_LABEL[p]}</span>
              <span className="text-grey-500 tabular-nums">
                {counted} of {SCALE} in 24h
                {counted > SCALE && <> · beyond the 50 scale</>}
              </span>
            </div>
            <div
              className="relative h-2 mt-1 bg-grey-100 rounded-full"
              role="img"
              aria-label={`${PLATFORM_LABEL[p]}: ${counted} of ${SCALE} in 24 hours (${posted} posted, ${s.queued_in_buffer} in Buffer)`}
            >
              <div className="absolute inset-0 flex gap-[2px] overflow-hidden rounded-full">
                {postedPct > 0 && (
                  <div className="h-full bg-primary-500" style={{ width: `${postedPct}%` }} title={`Posted in 24h: ${posted}`} />
                )}
                {bufferPct > 0 && (
                  <div
                    className="h-full bg-primary-200"
                    style={{ width: `${bufferPct}%` }}
                    title={`In Buffer: ${s.queued_in_buffer}`}
                  />
                )}
              </div>
            </div>
            <p className="text-xs text-grey-400 mt-1">
              {posted} posted · {s.queued_in_buffer} in Buffer · {waiting} drafts waiting
              {s.paused_until && <span className="text-warning-600"> · paused until {new Date(s.paused_until).toLocaleString()}</span>}
            </p>
          </div>
        );
      })}
      <p className="text-[11px] text-grey-400">Bars: 0–{SCALE} posts per 24h; dark = posted, light = in Buffer.</p>
    </div>
  );
}
